import { Injectable } from "@angular/core";
import { environment } from "./environment";

interface DirectUploadResponse {
	result: {
		id: string;
		uploadURL: string;
	};
	success: boolean;
}

interface UploadResponse {
	result: {
		id: string;
		filename: string;
	};
	success: boolean;
}

@Injectable({
	providedIn: "root",
})
export class CloudflareImagesService {
	private readonly accountId = environment.cloudflareAccountId;
	private readonly apiToken = environment.cloudflareImagesToken;

	/**
	 * Uploads an image directly to Cloudflare Images using Direct Creator Upload
	 * Returns the image ID which can be used to construct the image URL
	 *
	 * @param file - The image file to upload
	 * @returns Promise resolving to { imageId, filename }
	 * @throws Error if upload fails or credentials missing
	 */
	async uploadImage(
		file: File,
	): Promise<{ imageId: string; filename: string }> {
		if (!this.accountId || !this.apiToken) {
			throw new Error(
				"Cloudflare Images credentials not configured. Set VITE_CLOUDFLARE_ACCOUNT_ID and VITE_CLOUDFLARE_IMAGES_TOKEN in environment.",
			);
		}

		// Step 1: Request one-time upload URL
		const uploadUrl = await this.requestUploadUrl();

		// Step 2: Upload image to the one-time URL
		const response = await this.uploadImageToUrl(file, uploadUrl);

		return {
			imageId: response.result.id,
			filename: response.result.filename,
		};
	}

	/**
	 * Constructs the Cloudflare Images delivery URL for an image
	 * Assumes account_hash is the same as account_id for simplicity
	 *
	 * @param imageId - The image ID returned from upload
	 * @returns The public image URL
	 */
	getImageUrl(imageId: string): string {
		if (!this.accountId) {
			throw new Error("Cloudflare account ID not configured");
		}
		return `https://imagedelivery.net/${this.accountId}/${imageId}/public`;
	}

	/**
	 * Requests a one-time upload URL from Cloudflare
	 * Internal method used by uploadImage
	 */
	private async requestUploadUrl(): Promise<string> {
		const response = await fetch(
			`https://api.cloudflare.com/client/v4/accounts/${this.accountId}/images/v1/direct_upload`,
			{
				method: "POST",
				headers: {
					Authorization: `Bearer ${this.apiToken}`,
				},
			},
		);

		if (!response.ok) {
			const error = await response.json();
			throw new Error(
				`Failed to get upload URL: ${error.errors?.[0]?.message || response.statusText}`,
			);
		}

		const data = (await response.json()) as DirectUploadResponse;
		return data.result.uploadURL;
	}

	/**
	 * Uploads the image file to the one-time upload URL
	 * Internal method used by uploadImage
	 */
	private async uploadImageToUrl(
		file: File,
		uploadUrl: string,
	): Promise<UploadResponse> {
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetch(uploadUrl, {
			method: "POST",
			body: formData,
		});

		if (!response.ok) {
			const error = await response.json();
			throw new Error(
				`Failed to upload image: ${error.errors?.[0]?.message || response.statusText}`,
			);
		}

		return (await response.json()) as UploadResponse;
	}
}
